import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { 
  Bell, Mail, MessageSquare, Loader2, Save, 
  Trophy, DollarSign, Shield, TrendingUp, Megaphone,
  Smartphone, CheckCircle, Calendar, BarChart3, Send
} from 'lucide-react';
import api from '../lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NotificationSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [digestPreferences, setDigestPreferences] = useState({
    frequency: 'daily',
    include_trades: true,
    include_notifications: true,
    include_market_summary: false
  });
  const [preferences, setPreferences] = useState({
    // Email notifications
    email_welcome: true,
    email_challenge_purchased: true,
    email_phase_passed: true,
    email_funded: true,
    email_payout_eligible: true,
    email_withdrawal_submitted: true,
    email_withdrawal_approved: true,
    email_marketing: false,
    email_weekly_summary: true,
    // SMS notifications
    sms_enabled: false,
    sms_phone: '',
    sms_phase_passed: false,
    sms_funded: false,
    sms_withdrawal_approved: false,
  });

  useEffect(() => {
    fetchPreferences();
    fetchDigestPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/notifications/preferences`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPreferences(prev => ({
          ...prev,
          ...data.preferences
        }));
      }
    } catch (error) {
      console.error('Failed to fetch preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDigestPreferences = async () => {
    try {
      const response = await api.get('/digest/preferences');
      if (response.data) {
        setDigestPreferences(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch digest preferences:', error);
    }
  };

  const handleDigestFrequencyChange = async (frequency) => {
    try {
      const response = await api.put('/digest/preferences', { frequency });
      setDigestPreferences(response.data);
      toast.success(`Digest frequency updated to ${frequency}`);
    } catch (error) {
      toast.error('Failed to update digest preferences');
    }
  };

  const handleDigestToggle = async (key) => {
    try {
      const newValue = !digestPreferences[key];
      const response = await api.put('/digest/preferences', { [key]: newValue });
      setDigestPreferences(response.data);
    } catch (error) {
      toast.error('Failed to update digest preferences');
    }
  };

  const handleSendDigestNow = async (digestType) => {
    setSendingDigest(true);
    try {
      await api.post('/digest/send-now', { digest_type: digestType });
      toast.success(`${digestType.charAt(0).toUpperCase() + digestType.slice(1)} digest sent to your email!`);
    } catch (error) {
      toast.error('Failed to send digest. Check your email settings.');
    } finally {
      setSendingDigest(false);
    }
  };

  const handleToggle = (key) => {
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handlePhoneChange = (e) => {
    setPreferences(prev => ({
      ...prev,
      sms_phone: e.target.value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/notifications/preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(preferences)
      });
      
      if (response.ok) {
        toast.success('Notification preferences saved!');
      } else {
        toast.error('Failed to save preferences');
      }
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const emailNotifications = [
    {
      key: 'email_challenge_purchased',
      label: 'Challenge Purchased',
      description: 'Confirmation when you purchase a new challenge',
      icon: Trophy,
      required: false
    },
    {
      key: 'email_phase_passed',
      label: 'Phase Passed',
      description: 'Celebration when you complete a challenge phase',
      icon: TrendingUp,
      required: false
    },
    {
      key: 'email_funded',
      label: 'Funded Account',
      description: 'Congratulations when you become a funded trader',
      icon: CheckCircle,
      required: false
    },
    {
      key: 'email_payout_eligible',
      label: 'Payout Eligible',
      description: 'Notification when you can request a withdrawal',
      icon: DollarSign,
      required: false
    },
    {
      key: 'email_withdrawal_submitted',
      label: 'Withdrawal Submitted',
      description: 'Confirmation when your withdrawal is being processed',
      icon: Shield,
      required: false
    },
    {
      key: 'email_withdrawal_approved',
      label: 'Withdrawal Approved',
      description: 'Notification when your withdrawal is sent',
      icon: DollarSign,
      required: false
    },
    {
      key: 'email_weekly_summary',
      label: 'Weekly Summary',
      description: 'Weekly performance recap and trading insights',
      icon: Mail,
      required: false
    },
    {
      key: 'email_marketing',
      label: 'Promotions & Updates',
      description: 'Special offers, new features, and company updates',
      icon: Megaphone,
      required: false
    },
  ];

  const smsNotifications = [
    {
      key: 'sms_phase_passed',
      label: 'Phase Passed',
      description: 'SMS when you pass a phase',
      icon: TrendingUp,
    },
    {
      key: 'sms_funded',
      label: 'Funded Account',
      description: 'SMS when you become funded',
      icon: CheckCircle,
    },
    {
      key: 'sms_withdrawal_approved',
      label: 'Withdrawal Approved',
      description: 'SMS when withdrawal is processed',
      icon: DollarSign,
    },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-16">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-20" data-testid="notification-settings">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <Bell className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight">
                Notification <span className="text-primary">Settings</span>
              </h1>
              <p className="text-muted-foreground text-sm">
                Manage how and when we contact you
              </p>
            </div>
          </div>
        </motion.div>

        {/* Email Notifications */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="bg-card mb-6">
            <CardHeader>
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-primary" />
                <div>
                  <CardTitle className="font-heading uppercase">Email Notifications</CardTitle>
                  <CardDescription>Choose which emails you'd like to receive</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-1">
              {emailNotifications.map((notification, index) => {
                const Icon = notification.icon;
                return (
                  <div key={notification.key}>
                    <div 
                      className="flex items-center justify-between py-4 px-2 rounded-lg hover:bg-secondary/30 transition-colors"
                      data-testid={`notification-${notification.key}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-secondary/50 flex items-center justify-center">
                          <Icon className="w-5 h-5 text-muted-foreground" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-foreground">{notification.label}</span>
                            {notification.required && (
                              <Badge variant="outline" className="text-xs">Required</Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">{notification.description}</p>
                        </div>
                      </div>
                      <Switch
                        checked={preferences[notification.key]}
                        onCheckedChange={() => handleToggle(notification.key)}
                        disabled={notification.required}
                        data-testid={`switch-${notification.key}`}
                      />
                    </div>
                    {index < emailNotifications.length - 1 && <Separator className="my-1" />}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </motion.div>

        {/* SMS Notifications */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Card className="bg-card mb-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Smartphone className="w-5 h-5 text-primary" />
                  <div>
                    <CardTitle className="font-heading uppercase">SMS Notifications</CardTitle>
                    <CardDescription>Get instant alerts on your phone</CardDescription>
                  </div>
                </div>
                <Badge variant="outline" className="bg-yellow-500/10 text-yellow-500 border-yellow-500/30">
                  Coming Soon
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {/* SMS Enable Toggle */}
              <div className="flex items-center justify-between py-4 px-2 rounded-lg bg-secondary/30 mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <span className="font-medium text-foreground">Enable SMS Notifications</span>
                    <p className="text-sm text-muted-foreground">Receive important alerts via text message</p>
                  </div>
                </div>
                <Switch
                  checked={preferences.sms_enabled}
                  onCheckedChange={() => handleToggle('sms_enabled')}
                  disabled={true}
                  data-testid="switch-sms-enabled"
                />
              </div>

              {/* Phone Number Input */}
              <div className="mb-6 px-2">
                <Label htmlFor="phone" className="text-muted-foreground">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+1 (555) 000-0000"
                  value={preferences.sms_phone || ''}
                  onChange={handlePhoneChange}
                  disabled={true}
                  className="mt-2 bg-secondary/50"
                  data-testid="input-sms-phone"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  SMS notifications will be available soon. Enter your number to be notified when it launches.
                </p>
              </div>

              {/* SMS Options */}
              <div className="space-y-1 opacity-50">
                {smsNotifications.map((notification, index) => {
                  const Icon = notification.icon;
                  return (
                    <div key={notification.key}>
                      <div className="flex items-center justify-between py-4 px-2 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-secondary/50 flex items-center justify-center">
                            <Icon className="w-5 h-5 text-muted-foreground" />
                          </div>
                          <div>
                            <span className="font-medium text-foreground">{notification.label}</span>
                            <p className="text-sm text-muted-foreground">{notification.description}</p>
                          </div>
                        </div>
                        <Switch
                          checked={preferences[notification.key]}
                          onCheckedChange={() => handleToggle(notification.key)}
                          disabled={true}
                        />
                      </div>
                      {index < smsNotifications.length - 1 && <Separator className="my-1" />}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Email Digest Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
        >
          <Card className="bg-card mb-6 border-primary/20">
            <CardHeader>
              <div className="flex items-center gap-3">
                <BarChart3 className="w-5 h-5 text-primary" />
                <div>
                  <CardTitle className="font-heading uppercase">Trading Digest Emails</CardTitle>
                  <CardDescription>
                    Receive daily or weekly summaries of your trading performance
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Frequency Selection */}
              <div>
                <Label className="text-sm font-medium mb-3 block">Digest Frequency</Label>
                <div className="grid grid-cols-3 gap-3">
                  {['daily', 'weekly', 'none'].map((freq) => (
                    <button
                      key={freq}
                      onClick={() => handleDigestFrequencyChange(freq)}
                      className={`p-4 rounded-lg border-2 transition-all ${
                        digestPreferences.frequency === freq
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border hover:border-primary/50 text-muted-foreground hover:text-foreground'
                      }`}
                      data-testid={`digest-freq-${freq}`}
                    >
                      <div className="flex flex-col items-center gap-2">
                        {freq === 'daily' && <Calendar className="w-5 h-5" />}
                        {freq === 'weekly' && <BarChart3 className="w-5 h-5" />}
                        {freq === 'none' && <Bell className="w-5 h-5 opacity-50" />}
                        <span className="text-sm font-medium capitalize">{freq === 'none' ? 'Disabled' : freq}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {digestPreferences.frequency !== 'none' && (
                <>
                  <Separator />
                  
                  {/* Digest Content Options */}
                  <div className="space-y-4">
                    <Label className="text-sm font-medium">Include in Digest</Label>
                    
                    <div className="flex items-center justify-between py-2">
                      <div className="flex items-center gap-3">
                        <TrendingUp className="w-4 h-4 text-green-500" />
                        <div>
                          <span className="font-medium">Trading Summary</span>
                          <p className="text-sm text-muted-foreground">P&L, win rate, trade count</p>
                        </div>
                      </div>
                      <Switch
                        checked={digestPreferences.include_trades}
                        onCheckedChange={() => handleDigestToggle('include_trades')}
                        data-testid="digest-include-trades"
                      />
                    </div>
                    
                    <div className="flex items-center justify-between py-2">
                      <div className="flex items-center gap-3">
                        <Bell className="w-4 h-4 text-cyan-500" />
                        <div>
                          <span className="font-medium">Notifications Summary</span>
                          <p className="text-sm text-muted-foreground">Alerts and important updates</p>
                        </div>
                      </div>
                      <Switch
                        checked={digestPreferences.include_notifications}
                        onCheckedChange={() => handleDigestToggle('include_notifications')}
                        data-testid="digest-include-notifications"
                      />
                    </div>
                  </div>

                  <Separator />

                  {/* Send Test Digest */}
                  <div className="flex items-center justify-between p-4 rounded-lg bg-secondary/30">
                    <div>
                      <p className="font-medium">Send Test Digest</p>
                      <p className="text-sm text-muted-foreground">Preview what your digest looks like</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSendDigestNow('daily')}
                        disabled={sendingDigest}
                        data-testid="send-daily-digest"
                      >
                        {sendingDigest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                        Daily
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSendDigestNow('weekly')}
                        disabled={sendingDigest}
                        data-testid="send-weekly-digest"
                      >
                        {sendingDigest ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                        Weekly
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>

        {/* Save Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex justify-end"
        >
          <Button 
            onClick={handleSave} 
            disabled={saving}
            className="bg-primary hover:bg-primary/90"
            data-testid="save-preferences"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Preferences
          </Button>
        </motion.div>
      </div>
    </div>
  );
};

export default NotificationSettings;
